import React, { useEffect, useState } from "react";
import format from "html-format";
import { makeStyles } from "@mui/styles";
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  TextField,
} from "@mui/material";
import { callAPI } from "../../App/Helpers/callApi";
import Signifier from "../../App/Components/Signifier/Signifier";
import { useSelector } from "react-redux";
import { selectCareersClientValue } from "../../App/State/Slices/SelectedCareersClientSlice";
import Breadcrumb from "../../App/Components/Navigation/Breadcrumbs/Breadcrumbs";
import { ColorPicker, createColor } from "mui-color";
import DropdownComponent from "../../App/Components/Dropdown/DropdownComponent";
import { editBanner, getBanner } from "./BannerService";

function BannerPage() {
  const useStyles = makeStyles((theme) => ({
    root: {
      width: "100%",
      marginBottom: "50px",
      [theme.breakpoints.down("md")]: {
        width: "100%",
      },
    },
    cardContainer: {
      [theme.breakpoints.down("md")]: {
        width: "100%",
        overflowY: "scroll",
      },
      display: "flex",
      flexDirection: "column",
      marginBottom: "20px",
      width: "60%",
    },
    error: {
      color: theme.palette.error.main,
    },
    copy: {
      color: theme.palette.primary.copy,
    },
  }));

  const classes = useStyles();

  const selectedCareersClient = useSelector(selectCareersClientValue);
  const [showSignifier, setShowSignifier] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [inputError, setInputError] = useState(false);

  const [weights, setWeights] = useState([
    { val: "normal" },
    { val: "bold" },
    { val: "bolder" },
    { val: "lighter" },
  ]);
  const [decorations, setDecorations] = useState([
    { val: "none" },
    { val: "underline" },
    { val: "overline underline" },
  ]);
  const [positions, setPositions] = useState([
    { val: "absolute" },
    { val: "relative" },
    { val: "sticky" },
    { val: "fixed" },
  ]);
  const [displays, setDisplays] = useState([
    { name: "Fade-out", val: "fo" },
    { name: "Permanent", val: "p" },
  ]);

  const [displayBanner, setDisplayBanner] = useState(false);
  const [html, setHtml] = useState("");
  const [backgroundColor, setBackgroundColor] = useState(createColor("#fff"));
  const [top, setTop] = useState("");
  const [right, setRight] = useState("");
  const [bottom, setBottom] = useState("");
  const [left, setLeft] = useState("");
  const [textColor, setTextColor] = useState(createColor("#fff"));
  const [textSize, setTextSize] = useState("");
  const [lineHeight, setLineHeight] = useState("");
  const [hyperlinkColor, setHyperlinkColor] = useState(createColor("#fff"));
  const [hyperlinkSize, setHyperlinkSize] = useState("");
  const [selectedTextWeight, setSelectedTextWeight] = useState({});
  const [selectedHyperlinkWeight, setSelectedHyperLinkWeight] = useState({});
  const [selectedDecoration, setSelectedDecoration] = useState({});
  const [selectedPosition, setSelectedPosition] = useState({});
  const [selectedDisplay, setSelectedDisplay] = useState({});
  const [fadeTime, setFadeTime] = useState(null);

  const getBannerData = (careersClientId) => {
    getBanner(careersClientId).then((response) => {
      setDisplayBanner(response.isVisible);
      setHtml(response.html);
      setBackgroundColor(createColor(response.backgroundColor));
      setTop(response.topPadding);
      setRight(response.rightPadding);
      setBottom(response.bottomPadding);
      setLeft(response.leftPadding);
      setTextColor(createColor(response.textColor));
      setTextSize(response.textSize);
      setLineHeight(response.lineHeight);
      setHyperlinkColor(createColor(response.linkColor));
      setHyperlinkSize(response.linkSize);
      setFadeTime(response.fadeOutTime);
      weights.forEach((weight) => {
        if (weight.val === response.linkWeight) {
          setSelectedHyperLinkWeight(weight);
        }
        if (weight.val === response.textWeight) {
          setSelectedTextWeight(weight);
        }
      });
      decorations.forEach((dec) => {
        if (dec.val === response.linkDecoration) {
          setSelectedDecoration(dec);
        }
      });
      positions.forEach((pos) => {
        if (pos.val === response.position) {
          setSelectedPosition(pos);
        }
      });
      displays.forEach((dis) => {
        if (dis.val === response.displayOption) {
          setSelectedDisplay(dis);
        }
      });
    });
  };

  function saveBanner() {
    if (html === "" || html === undefined || html === null) {
      setInputError(true);
      return;
    }

    let data = {
      ClientId: selectedCareersClient.id,
      Html: html,
      BackgroundColor: "#" + backgroundColor.hex,
      LeftPadding: left,
      RightPadding: right,
      TopPadding: top,
      BottomPadding: bottom,
      TextColor: "#" + textColor.hex,
      TextSize: textSize,
      TextWeight: selectedTextWeight.val,
      LinkColor: "#" + hyperlinkColor.hex,
      LinkSize: hyperlinkSize,
      LinkWeight: selectedHyperlinkWeight.val,
      LineHeight: lineHeight,
      LinkDecoration: selectedDecoration.val,
      IsVisible: displayBanner,
      Position: selectedPosition.val,
      DisplayOption: selectedDisplay.val,
      FadeOutTime: fadeTime === null ? 0 : fadeTime,
    };

    callAPI(
      editBanner(data),
      "Banner Saved",
      setShowSignifier,
      setResponseMessage,
      setShowError,
      setProcessing
    ).then(() => {
      getBannerData(selectedCareersClient.id);
    });
  }

  useEffect(() => {
    setDisplayBanner(false);
    setHtml("");
    setBackgroundColor(createColor("#fff"));
    setTop("");
    setRight("");
    setBottom("");
    setLeft("");
    setTextColor(createColor("#fff"));
    setTextSize("");
    setLineHeight("");
    setHyperlinkColor(createColor("#fff"));
    setHyperlinkSize("");
    setSelectedHyperLinkWeight({});
    setSelectedTextWeight({});
    setSelectedDecoration({});
    setSelectedPosition({});
    setSelectedDisplay({});
    setFadeTime(null);
    if (selectedCareersClient !== null) {
      getBannerData(selectedCareersClient.id);
    }
  }, [selectedCareersClient]); // DANTODO: Ask David why this is yellow

  return (
    <>
      <Signifier
        showSignifier={showSignifier}
        setShowSignifier={setShowSignifier}
        message={responseMessage}
        error={showError}
      />
      <div className="page-container">
        <Breadcrumb />
        <h1 className="page-title">{selectedCareersClient.shortName} Banner</h1>
        <div className={classes.root}>
          <Grid className={classes.cardContainer}>
            <Card className={classes.card}>
              <CardContent>
                <Grid container spacing={1}>
                  <Grid xs={12} sm={12} item>
                    <FormControlLabel
                      className={classes.copy}
                      style={{ marginTop: "10px" }}
                      control={
                        <Checkbox
                          color="primary"
                          checked={displayBanner}
                          onChange={() => setDisplayBanner(!displayBanner)}
                        />
                      }
                      label="Display Banner"
                    />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <TextField
                      multiline
                      placeholder="HTML Text"
                      label="HTML Text"
                      variant="standard"
                      fullWidth
                      required
                      InputProps={{
                        className: classes.copy,
                      }}
                      error={
                        inputError &&
                        (html === null || html === undefined || html === "")
                      }
                      value={html === null ? html : format(html)}
                      onChange={(e) => setHtml(e.target.value)}
                    />
                    {inputError &&
                      (html === null || html === undefined || html === "") && (
                        <FormHelperText className={classes.error}>
                          HTML Required
                        </FormHelperText>
                      )}
                  </Grid>
                  <Grid item xs={12} sm={12} style={{ paddingTop: "15px" }}>
                    <FormHelperText>Background Color</FormHelperText>
                    <ColorPicker
                      value={backgroundColor}
                      onChange={(value) => setBackgroundColor(value)}
                      className={classes.copy}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      placeholder="Top Padding"
                      label="Top Padding"
                      variant="standard"
                      fullWidth
                      required
                      InputProps={{
                        className: classes.copy,
                      }}
                      value={top}
                      onChange={(e) => setTop(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      placeholder="Right Padding"
                      label="Right Padding"
                      variant="standard"
                      fullWidth
                      required
                      InputProps={{
                        className: classes.copy,
                      }}
                      value={right}
                      onChange={(e) => setRight(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      placeholder="Bottom Padding"
                      label="Bottom Padding"
                      variant="standard"
                      fullWidth
                      required
                      InputProps={{
                        className: classes.copy,
                      }}
                      value={bottom}
                      onChange={(e) => setBottom(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      placeholder="Left Padding"
                      label="Left Padding"
                      variant="standard"
                      fullWidth
                      required
                      InputProps={{
                        className: classes.copy,
                      }}
                      value={left}
                      onChange={(e) => setLeft(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} style={{ paddingTop: "15px" }}>
                    <FormHelperText>Text Color</FormHelperText>
                    <ColorPicker
                      value={textColor}
                      onChange={(value) => setTextColor(value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      placeholder="Text Size"
                      label="Text Size"
                      variant="standard"
                      fullWidth
                      required
                      InputProps={{
                        className: classes.copy,
                      }}
                      value={textSize}
                      onChange={(e) => setTextSize(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <DropdownComponent
                      currValue={selectedTextWeight}
                      items={weights}
                      property="val"
                      title="Text Weight"
                      handleChange={(value) => setSelectedTextWeight(value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      placeholder="Line Height"
                      label="Line Height"
                      variant="standard"
                      fullWidth
                      required
                      InputProps={{
                        className: classes.copy,
                      }}
                      value={lineHeight}
                      onChange={(e) => setLineHeight(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} style={{ paddingTop: "15px" }}>
                    <FormHelperText>Hyperlink Color</FormHelperText>
                    <ColorPicker
                      value={hyperlinkColor}
                      onChange={(value) => setHyperlinkColor(value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      placeholder="Hyperlink Size"
                      label="Hyperlink Size"
                      variant="standard"
                      fullWidth
                      required
                      InputProps={{
                        className: classes.copy,
                      }}
                      value={hyperlinkSize}
                      onChange={(e) => setHyperlinkSize(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <DropdownComponent
                      currValue={selectedHyperlinkWeight}
                      items={weights}
                      property="val"
                      title="Hyperlink Weight"
                      handleChange={(value) =>
                        setSelectedHyperLinkWeight(value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <DropdownComponent
                      currValue={selectedDecoration}
                      items={decorations}
                      property="val"
                      title="Hyperlink Decoration"
                      handleChange={(value) => setSelectedDecoration(value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <DropdownComponent
                      currValue={selectedPosition}
                      items={positions}
                      property="val"
                      title="Position"
                      handleChange={(value) => setSelectedPosition(value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <DropdownComponent
                      currValue={selectedDisplay}
                      items={displays}
                      property="name"
                      title="Display Options"
                      handleChange={(value) => setSelectedDisplay(value)}
                    />
                  </Grid>
                  {selectedDisplay.val === "fo" ? (
                    <Grid item xs={12} sm={4}>
                      <TextField
                        placeholder="Fade-out Time (seconds)"
                        label="Fade-out Time (seconds)"
                        variant="standard"
                        fullWidth
                        required
                        type="number"
                        InputProps={{
                          className: classes.copy,
                        }}
                        value={fadeTime}
                        onChange={(e) => setFadeTime(e.target.value)}
                      />
                    </Grid>
                  ) : null}
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    container
                    justifyContent="flex-end"
                  >
                    <Button
                      style={{ marginTop: "20px", marginRight: "8px" }}
                      className={classes.sensitiveButton}
                      type="submit"
                      variant="contained"
                      color="primary"
                      onClick={() => saveBanner()}
                    >
                      Save Information
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </div>
      </div>
    </>
  );
}

export default BannerPage;
